import { isNil } from 'lodash'
import { useRouter } from 'next/router'
import { useMemo } from 'react'

import useCompanyBenefits from '@/legacy-src/hooks/useCompanyBenefits'

import { useGetFilteredTherapistList } from '@/src/domains/therapist/hooks/useGetFilteredTherapistList'

import useServices from '../../../hooks/useServices'
import useTherapists from '../../../hooks/useTherapists'
import useUser from '../../../hooks/useUser'
import { TherapistStatus, type TherapistPriceType, type TherapistType } from '../../../types/TherapistStateType'
import { sanitizeTextForSearch } from '../../../utilities/textUtilities'
import {
  FILTER_INSURANCE_SLUG,
  FILTER_LANGUAGE_SLUG,
  FILTER_SEARCH_SLUG,
  FILTER_SPECIALIZATION_SLUG,
  FILTER_TRAINING_SLUG,
} from '../../filters/filters.constants'
import { sortTherapists } from '../utils/sortTherapists'

const isTherapistVisible = (therapist: TherapistType) => therapist.visible && therapist.freeSchedulesCountLongTerm > 0

const isSearchedTherapist = (therapist: TherapistType, searchName: string) => {
  const searchWords = searchName
    .toLowerCase()
    .split(' ')
    .filter((word) => word)

  const firstName = sanitizeTextForSearch(therapist.firstName)
  const lastName = sanitizeTextForSearch(therapist.lastName)

  return searchWords.some((searchWord) => {
    const normalizedSearchWord = sanitizeTextForSearch(searchWord)

    return firstName.indexOf(normalizedSearchWord) !== -1 || lastName.indexOf(normalizedSearchWord) !== -1
  })
}

const hasSelectedOptions = (therapist: TherapistType, selectedValues: string | string[], keys: string[]) => {
  if (!selectedValues) return true

  const values = typeof selectedValues === 'string' ? selectedValues.split(',') : selectedValues
  const firstFoundMissingValue = values.find((value) => {
    return !keys.find((key) => {
      const isNumericString = !isNaN(Number(value))
      const compareValue = isNumericString ? Number(value) : value
      return therapist[key].includes(compareValue)
    })
  })

  return !Boolean(firstFoundMissingValue)
}

const useFilteredTherapists = (isPublic: boolean) => {
  const { therapists, isLoadingTherapists } = useTherapists('all')
  const { selectedServiceId, selectedService, isLoadingServices } = useServices()
  const { query } = useRouter()
  const { data: therapistList, isLoading: isLoadingTimeFilter } = useGetFilteredTherapistList()
  const { user } = useUser()
  const { quotas } = useCompanyBenefits()

  const filteredTherapists = useMemo(() => {
    const searchName = query[FILTER_SEARCH_SLUG] as string
    const therapistData = therapistList ?? therapists

    return therapistData !== undefined && therapistData.length > 0
      ? therapistData.filter((therapist: TherapistType) => {
          if (therapist.status === TherapistStatus.TESTING) return false
          if (!therapist.services?.includes(selectedServiceId)) return false
          if (
            !hasSelectedOptions(therapist, query[FILTER_SPECIALIZATION_SLUG], [
              'majorSpecializations',
              'minorSpecializations',
            ])
          )
            return false
          if (!hasSelectedOptions(therapist, query[FILTER_TRAINING_SLUG], ['trainings'])) return false
          if (!hasSelectedOptions(therapist, query[FILTER_LANGUAGE_SLUG], ['languages'])) return false
          if (!hasSelectedOptions(therapist, query[FILTER_INSURANCE_SLUG], ['insurances'])) return false
          if (searchName) return isSearchedTherapist(therapist, searchName)

          return true
        })
      : []
  }, [therapists, query, therapistList, selectedServiceId])

  const visibleTherapists = useMemo(() => {
    return filteredTherapists.filter(isTherapistVisible)
  }, [filteredTherapists])

  const hiddenTherapists = useMemo(() => {
    return filteredTherapists.filter((therapist) => !isTherapistVisible(therapist))
  }, [filteredTherapists])

  const quotaForService = quotas?.find((quota) => quota.services.includes(selectedServiceId as number))

  const visitedTherapists = useMemo(() => {
    return filteredTherapists.filter((therapist) => {
      return user?.visitedTherapistIds?.includes(therapist.therapistId) ?? false
    })
  }, [filteredTherapists, user])

  const visibleTherapistsNotExceedingMaxPrice = useMemo(() => {
    return visibleTherapists.filter((therapist) => {
      const priceForSelectedService =
        therapist.prices?.find((item: TherapistPriceType) => item.serviceId === selectedServiceId)?.price ?? 0

      return (
        isNil(quotaForService?.maxPrice) ||
        (priceForSelectedService > 0 && priceForSelectedService <= quotaForService?.maxPrice)
      )
    })
  }, [visibleTherapists, quotaForService, selectedServiceId])

  const therapistsForAUser = useMemo(() => {
    const therapistsWithoutDuplicates = quotaForService
      ? [...visibleTherapistsNotExceedingMaxPrice, ...visitedTherapists]
      : [...visibleTherapists, ...visitedTherapists]

    return therapistsWithoutDuplicates.filter(
      (therapist, index, array) => array.findIndex((t) => t.therapistId === therapist.therapistId) === index,
    )
  }, [visitedTherapists, visibleTherapists, visibleTherapistsNotExceedingMaxPrice, quotaForService])

  const therapistsToDisplay = useMemo(() => {
    const list = isPublic ? visibleTherapists : therapistsForAUser
    return sortTherapists(list, query, selectedServiceId)
  }, [isPublic, visibleTherapists, therapistsForAUser, query, selectedServiceId])

  return {
    therapists: selectedService ? therapistsToDisplay : [],
    isLoadingList: isLoadingServices || isLoadingTherapists,
    isLoadingTimeFilter,
    hiddenTherapists,
  }
}

export default useFilteredTherapists
